// "Add a recipe" (wireframe 10): from a web page, written yourself, or from
// a file. Every route ends on the same review screen (RecipeWriterPage): a
// link or file hands what it found to /recipes/import; "Write it yourself"
// opens the four steps at /recipes/new.
import { useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { COLORS, SPACE } from "../../lib/constants";
import type { ImportHandoff } from "../../pages/RecipeWriterPage";
import { Sheet } from "../Sheet";
import { smallLabel, supporting } from "../recipeStyles";
import { inputStyle, panel } from "../plan/planStyles";
import { primaryButton, secondaryButton } from "./writeStyles";

export function AddRecipeSheet({ onClose }: { onClose: () => void }) {
  const mp = useMealPlanner();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const review = (handoff: ImportHandoff) => navigate("/recipes/import", { state: handoff });

  async function importUrl() {
    if (!url.trim() || busy) return;
    setError("");
    setBusy(true);
    try {
      const { recipe, dedupRecipeId, normalizedUrl } = await mp.importRecipeFromUrl(url.trim());
      const found = recipe as Record<string, unknown>;
      if (!String(found?.title || "").trim()) {
        setError("Couldn't find a recipe at that link. You can write it in yourself.");
        return;
      }
      review({ recipes: [found], urlMeta: { dedupRecipeId, normalizedUrl } });
    } catch (e) {
      setError((e as Error).message || "Couldn't import a recipe from that link.");
    } finally {
      setBusy(false);
    }
  }

  function readFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result));
        const list = Array.isArray(data) ? data : Array.isArray(data.recipes) ? data.recipes : null;
        if (!list) {
          setError('That file doesn\'t look like a recipe list: expected a list of recipes, or an object with a "recipes" list.');
          return;
        }
        const recipes = (list as Record<string, unknown>[]).filter((r) => r && String(r.title || "").trim());
        if (recipes.length === 0) {
          setError("No recipes with a name were found in that file.");
          return;
        }
        review({ recipes });
      } catch {
        setError("Couldn't read that file. It needs to be a recipe file (JSON).");
      }
    };
    reader.readAsText(file);
  }

  return (
    <Sheet title="Add a recipe" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
        <section style={panel}>
          <h3 style={{ ...smallLabel, margin: "0 0 var(--space-sm)" }}>From a web page</h3>
          <div style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
            <input
              type="url"
              aria-label="Paste the link"
              placeholder="Paste the link"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && importUrl()}
              disabled={busy}
              style={{ ...inputStyle, flex: "1 1 200px", minWidth: 0 }}
            />
            <button type="button" onClick={importUrl} disabled={busy || !url.trim()} style={{ ...primaryButton, opacity: busy || !url.trim() ? 0.6 : 1 }}>
              {busy ? "Fetching…" : "Import"}
            </button>
          </div>
          <p style={{ ...supporting, margin: "var(--space-sm) 0 0" }}>You review the recipe before it is saved. Some sites block importing; if that happens you can write it in yourself.</p>
        </section>

        <section style={panel}>
          <h3 style={{ ...smallLabel, margin: "0 0 var(--space-sm)" }}>Write it yourself</h3>
          <p style={{ ...supporting, margin: "0 0 var(--space-sm)" }}>Four short steps: basics, ingredients, steps, details. It&apos;s kept as a draft as you go.</p>
          <button type="button" onClick={() => navigate("/recipes/new")} style={secondaryButton}>
            Start
          </button>
        </section>

        <section style={panel}>
          <h3 style={{ ...smallLabel, margin: "0 0 var(--space-sm)" }}>From a file</h3>
          <p style={{ ...supporting, margin: "0 0 var(--space-sm)" }}>A recipe file exported from The Weekly Kitchen (JSON).</p>
          <label style={{ ...secondaryButton, display: "inline-flex", alignItems: "center" }}>
            Choose a file
            <input type="file" accept="application/json,.json" onChange={readFile} style={{ display: "none" }} />
          </label>
        </section>

        {error && <p style={{ color: COLORS.danger, margin: 0 }}>{error}</p>}
        <p style={{ ...supporting, margin: 0 }}>Every route ends on the same review screen: check the name, ingredients and steps, then save.</p>
      </div>
    </Sheet>
  );
}
